import * as React from 'react';

import classnames from 'classnames';
import moment from 'moment';

import {ThreadType} from 'nutshell-graphql-types';
import type {ErrorReason, NutEmailAddress} from 'nutshell-graphql-types';

import {Button} from '../../../button/button';
import {colors} from '../../../colors/colors';
import {DoubleTickIcon} from '../../../icon/svg/double-tick-icon';
import {Popover} from '../../../popover/popover';
import {hasHtmlTags} from '../../../rich-text-display/helpers';
import {RichTextDisplay} from '../../../rich-text-display/rich-text-display';
import {FormattedTimestamp} from '../../../timestamp/formatted-timestamp';
import {Body} from '../../../typography/body';
import {useRefObserver} from '../../../utils/hooks/use-ref-observer';
import {linkifyString, linkifyWithCustomLink} from '../../../utils/linkify-string';

import {getFormattedTimestampForLastMessage} from '../../helpers';

import {ActionButtons} from './action-buttons';
import {getSecondaryButtonInfo, shouldShowActionButtons} from './helpers';
import type {DripSequenceInfo} from './helpers';
import {ImageGallery} from './image-gallery';
import {MessageStatusText} from './message-status-text';
import {SubmissionCard} from './submission-card';

import styles from './message-bubble.module.css';

export type Props = {
    messageId: string;
    message: string;
    mediaUrl: string | null | undefined;
    senderEmailAddress: NutEmailAddress | null | undefined;
    isOutbound: boolean;
    isSending: boolean;
    isScheduled: boolean;
    isFailed: boolean;
    isCancelled: boolean;
    isCancelable: boolean;
    scheduledTime: any;
    preventResend: boolean;
    dripSequenceInfo?: DripSequenceInfo;
    onResendMessage: (messageId: string) => Promise<any>;
    onDeleteMessage: (messageId: string) => Promise<any>;
    sentTime?: number;
    isLastEvent: boolean;
    reaction?: string;
    errorReason?: ErrorReason | null | undefined;
    isNutshellSystemResponse?: boolean;
    isLastReadMessage?: boolean;
    readTime?: number;
    images?: string[] | null | undefined;
    submission?: any | null | undefined;
    shouldShowTimestampPopover: boolean;
    threadType?: ThreadType;
    isSidebar?: boolean;
};

const COLLAPSED_LINE_HEIGHT = 22;
const MESSAGE_MAX_LINES = 8;
const EMAIL_MAX_LINES = 6;

export function MessageBubble(props: Props) {
    const [isResending, setIsResending] = React.useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
    const [isExpanded, setIsExpanded] = React.useState<boolean>(false);
    const [shouldShowExpandButton, setShouldShowExpandButton] = React.useState<boolean>(false);

    const messageRef = React.useRef(null);
    const messageTextRef = React.useRef<HTMLDivElement | null>(null);
    const {isHovered} = useRefObserver(messageRef, ['isHovered']);

    const collapsedMaxLines =
        props.threadType === ThreadType.EMAIL ? EMAIL_MAX_LINES : MESSAGE_MAX_LINES;
    const collapsedMaxHeight = COLLAPSED_LINE_HEIGHT * collapsedMaxLines;
    const shouldTruncate = !isExpanded && shouldShowExpandButton;

    const hasImages = props.images && props.images.length > 0;
    const hasMessage = props.message && props.message.trim().length > 0;
    const hasSubmission = props.submission != null;

    React.useEffect(() => {
        if (
            hasMessage &&
            !hasSubmission &&
            messageTextRef.current &&
            messageTextRef.current.scrollHeight > collapsedMaxHeight
        ) {
            setShouldShowExpandButton(true);
        }
    }, [hasMessage, hasSubmission, props.message, collapsedMaxHeight]);

    const isDueToSend = moment().isAfter(moment.unix(props.scheduledTime));

    const showActionButtons = shouldShowActionButtons(
        isResending,
        isDueToSend,
        props.isScheduled,
        props.isFailed
    );

    const messageContainerStylenames = classnames({
        [styles['message-container']]: true,
        [styles['message-container--outbound']]: props.isOutbound,
        [styles['message-container--with-images']]: hasImages,
        [styles['message-container--sidebar']]: props.isSidebar,
    });

    const messageStylenames = classnames({
        [styles.message]: true,
        [styles['message--sending']]: props.isSending,
        [styles['message--scheduled']]: props.isScheduled,
        [styles['message--failed']]: props.isFailed,
        [styles['message--outbound']]: props.isOutbound,
        [styles['message--system']]: props.isNutshellSystemResponse,
        [styles['message--with-images']]: hasImages,
        [styles['message--images-only']]: hasImages && !hasMessage,
    });

    const timestampStylenames = classnames({
        [styles['timestamp--with-reaction-outbound']]: props.reaction && props.isOutbound,
        [styles['timestamp--with-reaction-inbound']]: props.reaction && !props.isOutbound,
    });

    const statusMessagesStylenames = classnames(styles['status-messages'], {
        [styles['status-messages--without-action-buttons']]: !showActionButtons,
    });

    const handleResendMessage = () => {
        setIsResending(true);
        props.onResendMessage(props.messageId);
    };

    const isHtml = hasHtmlTags(props.message);

    let displayableMessage;
    if (props.mediaUrl) {
        displayableMessage = linkifyWithCustomLink(props.message, props.mediaUrl);
    } else if (!isHtml) {
        displayableMessage = linkifyString(props.message);
    }

    const positionInfo =
        props.dripSequenceInfo &&
        `${props.dripSequenceInfo.position}/${props.dripSequenceInfo.total}`;

    return (
        <>
            <div className={messageContainerStylenames}>
                {(hasMessage || hasImages || hasSubmission) && (
                    <div className={messageStylenames}>
                        <div ref={messageRef} className={styles['message-content']}>
                            {props.senderEmailAddress && (
                                <div className='flex gap-8 mb-8'>
                                    <Body size='small' color='dark' isBold={true}>
                                        {props.senderEmailAddress.display}
                                    </Body>
                                    <Body size='small' color='light'>
                                        {props.senderEmailAddress.address}
                                    </Body>
                                </div>
                            )}
                            {hasImages && (
                                <ImageGallery
                                    images={props.images}
                                    isOutbound={props.isOutbound}
                                    isModalOpen={isModalOpen}
                                    setIsModalOpen={setIsModalOpen}
                                />
                            )}
                            {hasMessage && !hasSubmission && (
                                <div ref={messageTextRef} className={styles['message-text']}>
                                    {isHtml ? (
                                        <RichTextDisplay
                                            html={props.message}
                                            size='small'
                                            maxLines={
                                                shouldTruncate ? collapsedMaxLines : undefined
                                            }
                                        />
                                    ) : (
                                        <Body
                                            size='small'
                                            truncate={
                                                shouldTruncate
                                                    ? {type: 'block', maxLines: collapsedMaxLines}
                                                    : undefined
                                            }
                                            maintainLineBreaks={!isHtml}
                                        >
                                            {displayableMessage}
                                        </Body>
                                    )}
                                </div>
                            )}
                            {hasSubmission && (
                                <SubmissionCard
                                    submission={props.submission}
                                    messageContent={displayableMessage}
                                />
                            )}
                        </div>
                        {shouldShowExpandButton && (
                            <Button
                                variant='text-secondary'
                                size='tiny'
                                noPadding={true}
                                alwaysUnderline={true}
                                onClick={() => setIsExpanded(!isExpanded)}
                            >
                                {isExpanded ? 'See less' : 'See more'}
                            </Button>
                        )}
                    </div>
                )}
                {(props.isFailed ||
                    props.isCancelled ||
                    (props.isScheduled && props.scheduledTime) ||
                    showActionButtons) && (
                    <div className={styles['message-footer']}>
                        <div className={statusMessagesStylenames}>
                            {props.isFailed && (
                                <MessageStatusText
                                    status='FAILED'
                                    errorReason={props.errorReason}
                                />
                            )}
                            {props.isCancelled && <MessageStatusText status='CANCELLED' />}
                            {props.isScheduled && props.scheduledTime && (
                                <div className='flex gap-8'>
                                    <MessageStatusText
                                        status='SCHEDULED'
                                        scheduledTime={props.scheduledTime}
                                    />
                                    {positionInfo && (
                                        <div
                                            className='flex gap-4'
                                            title="The message's position in the drip sequence"
                                        >
                                            <span aria-label='drip sequence'>&#x1F6B0;</span>
                                            <Body size='small' color='light'>
                                                {positionInfo}
                                            </Body>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        {!showActionButtons && props.sentTime && (
                            <div className='flex align-center gap-4 mt-4'>
                                {props.isLastReadMessage && (
                                    <DoubleTickIcon
                                        size={16}
                                        // @ts-expect-error
                                        fill={colors.orange}
                                        alignCenter={true}
                                        wrapWithDiv={false}
                                    />
                                )}
                                {(props.isLastEvent || props.isLastReadMessage) && (
                                    <div className={timestampStylenames}>
                                        <Body size='mini' color='light' nowrap={true}>
                                            {getFormattedTimestampForLastMessage(props.sentTime)}
                                        </Body>
                                    </div>
                                )}
                            </div>
                        )}
                        {showActionButtons && (
                            <div className='nowrap flex-grow'>
                                <ActionButtons
                                    preventResend={props.preventResend}
                                    primaryButtonInfo={{
                                        text: props.isScheduled ? 'Send now' : 'Retry',
                                        onClick: handleResendMessage,
                                    }}
                                    secondaryButtonInfo={
                                        props.isCancelable
                                            ? getSecondaryButtonInfo(props.dripSequenceInfo, () =>
                                                  props.onDeleteMessage(props.messageId)
                                              )
                                            : undefined
                                    }
                                />
                            </div>
                        )}
                    </div>
                )}
                {props.reaction && <div className={styles.reaction}>{props.reaction}</div>}
                {showActionButtons && props.sentTime && (
                    <div className='flex align-center justify-end pr-4 mt-2 gap-4'>
                        {props.isLastReadMessage && (
                            <DoubleTickIcon
                                size={16}
                                // @ts-expect-error
                                fill={colors.orange}
                                alignCenter={true}
                                wrapWithDiv={false}
                            />
                        )}
                        {(props.isLastEvent || props.isLastReadMessage) && (
                            <div className={timestampStylenames}>
                                <Body size='mini' color='light' nowrap={true}>
                                    {getFormattedTimestampForLastMessage(props.sentTime)}
                                </Body>
                            </div>
                        )}
                    </div>
                )}
                {!showActionButtons &&
                    !(
                        props.isFailed ||
                        props.isCancelled ||
                        (props.isScheduled && props.scheduledTime)
                    ) &&
                    props.sentTime && (
                        <div className='flex align-center justify-end pr-4 mt-2 gap-4'>
                            {props.isLastReadMessage && (
                                <DoubleTickIcon
                                    size={16}
                                    // @ts-expect-error
                                    fill={colors.orange}
                                    alignCenter={true}
                                    wrapWithDiv={false}
                                />
                            )}
                            {(props.isLastEvent || props.isLastReadMessage) && (
                                <div className={timestampStylenames}>
                                    <Body size='mini' color='light' nowrap={true}>
                                        {getFormattedTimestampForLastMessage(props.sentTime)}
                                    </Body>
                                </div>
                            )}
                        </div>
                    )}
            </div>
            {isHovered && props.sentTime && !isModalOpen && props.shouldShowTimestampPopover && (
                <Popover
                    anchor={messageRef.current}
                    location='top'
                    isNubDark={true}
                    bodyColor='dark'
                >
                    <div className='pad-8'>
                        {props.isLastReadMessage && 'Read at: '}
                        {moment
                            .unix(
                                props.isLastReadMessage && props.readTime
                                    ? props.readTime
                                    : props.sentTime
                            )
                            .format(FormattedTimestamp.formats.longMonthDayYearWithTime)}
                    </div>
                </Popover>
            )}
        </>
    );
}
